import React, { FunctionComponent } from 'react';
import { useForm, Controller } from 'react-hook-form';
import moment, { Moment } from 'moment';

import { SubmitButton } from 'components';
import { FormGroup, DatePicker } from 'components/chroma';

import {
  StandardValueType,
  SingleFieldStepProps,
  ValueMapperParams,
} from 'scenes/common/types';
import BottomLineHelpTextWrapper from './BottomLineHelpTextWrapper';

export const valueMapper = (
  bottomLineHelperTexts: ValueMapperParams,
  value: StandardValueType
) =>
  bottomLineHelperTexts.map((text) =>
    typeof text === 'function' ? text(value) : text
  );

const SingleDatePickerStep: FunctionComponent<
  SingleFieldStepProps<string> & {
    bottomLineHelperTexts?: ValueMapperParams;
    isOutsideRange: (day: Moment) => boolean;
    ariaLabel?: string;
    ariaDescription?: string;
  }
> = ({
  onNext,
  stateKey,
  question,
  helpText,
  isOutsideRange,
  defaultStateValue,
  bottomLineHelperTexts,
  ariaLabel,
  ariaDescription,
}) => {
  const {
    formState: { isValid },
    handleSubmit,
    control,
    errors,
  } = useForm({
    mode: 'onChange',
  });

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <Controller
        control={control}
        name={stateKey}
        defaultValue={defaultStateValue}
        rules={{
          validate: {
            validDate: (value) =>
              (value && !isOutsideRange(moment(value))) || 'Invalid date',
          },
        }}
        render={({ onChange, value }) => (
          <FormGroup
            id={stateKey}
            label={question}
            helpText={helpText ?? ''}
            error={errors[stateKey]?.message || ''}
          >
            <BottomLineHelpTextWrapper
              helpTexts={
                bottomLineHelperTexts && isValid
                  ? valueMapper(bottomLineHelperTexts, value)
                  : []
              }
            >
              <DatePicker
                id={stateKey}
                ariaLabel={ariaLabel}
                screenReaderInputMessage={ariaDescription}
                displayFormat="DD/MM/YYYY"
                isOutsideRange={isOutsideRange}
                numberOfMonths={1}
                value={value}
                onChange={onChange}
              />
            </BottomLineHelpTextWrapper>
          </FormGroup>
        )}
      />
      <SubmitButton enabled={isValid}>Next</SubmitButton>
    </form>
  );
};

export default SingleDatePickerStep;
